import { __awaiter, __extends, __generator, __read } from "tslib";
import { DrmCertificateManager, getCurrentPlayerType, loadPropositionExtensions, PlayerController, sdkLogger, SessionControllerPrecursor, SessionControllerPrecursorCache, isRawSessionItem, InternalConfigProvider, defaultCompiledConfigLoader, HdcpManager, } from '@sky-uk-ott/core-video-sdk-js-core';
import { CoreVideoInternal } from '@sky-uk-ott/core-video-sdk-js-core/lib/core-video-internal';
import { JsonUtils } from '@sky-uk-ott/core-video-sdk-js-core/lib/utils/json-utils';
import { loadPlayerEngineFactory } from './load-player-engine-factory';
var CoreVideoSdk = /** @class */ (function (_super) {
    __extends(CoreVideoSdk, _super);
    function CoreVideoSdk() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    Object.defineProperty(CoreVideoSdk, "isInitialised", {
        get: function () {
            return Boolean(CoreVideoInternal._isInitialised);
        },
        enumerable: false,
        configurable: true
    });
    /**
     * The main entry point to the framework which loads the relevant framework libraries required for the application and makes sure the device is initialised.
     * This **must** be called and **resolved** before any of the device specific functionality can be accessed.
     * @param sdkConfig Configuration describing the application.
     * @param deviceOrVpip Optional device instance or video platform integration provider
     * @param videoPlatformIntegrationProvider Video platform integration provider implementation if device is passed as second argument
     */
    CoreVideoSdk.initialise = function (sdkConfig, device, videoPlatformIntegrationProvider) {
        var _a, _b, _c, _d;
        return __awaiter(this, void 0, void 0, function () {
            var initLogger, configProvider, _e, config, failoverReason, propositionExtensions, playerEngineFactory, customVpip, shouldUseCustomVpip, vpip, e_1;
            return __generator(this, function (_f) {
                switch (_f.label) {
                    case 0:
                        if (CoreVideoInternal._isInitialised) {
                            throw new Error('Core SDK has already been initialised');
                        }
                        if (CoreVideoInternal._isInitialised !== null) {
                            throw new Error('Initialising of Core SDK has already been attempted');
                        }
                        CoreVideoInternal._isInitialised = false;
                        initLogger = sdkLogger.withContext('INIT');
                        // Ecmascript spec gets replaced in the webpack build process with the relevant source version
                        initLogger.info("Initialising SDK, version ".concat(this.version, ", using ES5 source code"));
                        _f.label = 1;
                    case 1:
                        _f.trys.push([1, 5, , 6]);
                        if (!this.device) {
                            this.device = device;
                        }
                        configProvider = new InternalConfigProvider(sdkConfig, this.device, defaultCompiledConfigLoader);
                        return [4 /*yield*/, configProvider.initialise()];
                    case 2:
                        _f.sent();
                        _e = __read(configProvider.getConfig(), 2), config = _e[0], failoverReason = _e[1];
                        CoreVideoInternal._configFailoverReason = failoverReason;
                        initLogger.verbose("\n\nFramework Config:\n".concat(JsonUtils.stringify(config), "\n\n"));
                        return [4 /*yield*/, this.initialisePropositionExtensions(config, initLogger)];
                    case 3:
                        propositionExtensions = _f.sent();
                        this.hdcpManager = ((_a = sdkConfig.vpi) === null || _a === void 0 ? void 0 : _a.useCustomVpip) ? undefined : new HdcpManager(device.capabilities);
                        return [4 /*yield*/, this.initialisePlayerEngineFactory(config, propositionExtensions.propositionPlayerConfig, propositionExtensions.thumbnailConfig, propositionExtensions.highFramerateValue)];
                    case 4:
                        playerEngineFactory = _f.sent();
                        this.configurePrefetching(config);
                        customVpip = videoPlatformIntegrationProvider;
                        shouldUseCustomVpip = ((_b = config.staticConfig.vpi) === null || _b === void 0 ? void 0 : _b.useCustomVpip) && customVpip;
                        vpip = shouldUseCustomVpip ? customVpip : propositionExtensions.vpiProvider;
                        if (!vpip) {
                            initLogger.error('No VPIP provided');
                            throw new Error('No VPIP provided');
                        }
                        CoreVideoInternal.register(config, configProvider, vpip, this.device, playerEngineFactory, propositionExtensions, this.hdcpManager);
                        CoreVideoInternal._isInitialised = true;
                        (_c = this.hdcpManager) === null || _c === void 0 ? void 0 : _c.initialise(playerEngineFactory.capabilities);
                        DrmCertificateManager.preload(config.staticConfig.drm);
                        (_d = propositionExtensions.securityLevelBasedCapManager) === null || _d === void 0 ? void 0 : _d.initialize(playerEngineFactory.capabilities, this.hdcpManager);
                        return [3 /*break*/, 6];
                    case 5:
                        e_1 = _f.sent();
                        initLogger.error('Framework initialisation failed', e_1);
                        throw e_1;
                    case 6: return [2 /*return*/];
                }
            });
        });
    };
    CoreVideoSdk.reInitialise = function () {
        return __awaiter(this, void 0, void 0, function () {
            var reInitLogger, _a, newConfig, failoverReason, newConfigId, existingConfigId, playerEngineFactory, cache;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        if (CoreVideoInternal._isInitialised === null || CoreVideoInternal._isInitialised === false) {
                            throw new Error('Core Video SDK must be initialised before it is re-initialised');
                        }
                        reInitLogger = sdkLogger.withContext('RE-INIT');
                        _a = __read(this.configProvider.getConfig(), 2), newConfig = _a[0], failoverReason = _a[1];
                        CoreVideoInternal._configFailoverReason = failoverReason;
                        newConfigId = newConfig.default.configMetadata.configIdentifier;
                        existingConfigId = this.config.default.configMetadata.configIdentifier;
                        reInitLogger.info("New Config ID: ".concat(newConfigId, " || Existing Config ID: ").concat(existingConfigId));
                        if (newConfigId === existingConfigId) {
                            reInitLogger.info('No config changes - skipping re-initialisation.');
                            return [2 /*return*/, this.config];
                        }
                        return [4 /*yield*/, this.initialisePlayerEngineFactory(newConfig, this.getPropositionExtensions().propositionPlayerConfig, this.getPropositionExtensions().thumbnailConfig)];
                    case 1:
                        playerEngineFactory = _b.sent();
                        cache = SessionControllerPrecursorCache.getInstance();
                        cache.clearCache();
                        this.configurePrefetching(newConfig);
                        CoreVideoInternal.reRegister(newConfig, playerEngineFactory);
                        reInitLogger.info('CVSDK Re-Initialisation Completed.');
                        return [2 /*return*/, newConfig];
                }
            });
        });
    };
    CoreVideoSdk.initialisePlayerEngineFactory = function (config, propositionPlayerConfig, propositionThumbnailConfig, propositionHighFramerateValue) {
        return __awaiter(this, void 0, void 0, function () {
            var playerType, playerEngineFactory;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        playerType = getCurrentPlayerType(config.staticConfig.proposition, this.device.type, this.device.deviceInfo, config.staticConfig.playerOverride);
                        return [4 /*yield*/, loadPlayerEngineFactory(playerType, 
                            // TODO: Move this into the PEFs so that they can create a PEI
                            // with different config based on the playback type
                            InternalConfigProvider.getForPlaybackType(config, 'default'), this.device, propositionPlayerConfig, propositionThumbnailConfig, propositionHighFramerateValue)];
                    case 1:
                        playerEngineFactory = _a.sent();
                        return [4 /*yield*/, playerEngineFactory.init()];
                    case 2:
                        _a.sent();
                        return [2 /*return*/, playerEngineFactory];
                }
            });
        });
    };
    CoreVideoSdk.configurePrefetching = function (config) {
        // TODO: Make precursor cache accept per-playback-type config
        SessionControllerPrecursorCache.configure(config.default.prefetching);
    };
    CoreVideoSdk.initialisePropositionExtensions = function (config, logger) {
        return __awaiter(this, void 0, void 0, function () {
            var propositionExtensions, e_2;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        logger.verbose('Loading proposition extensions');
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, , 4]);
                        return [4 /*yield*/, loadPropositionExtensions(config.staticConfig.proposition, config, this.device)];
                    case 2:
                        propositionExtensions = _a.sent();
                        return [2 /*return*/, propositionExtensions];
                    case 3:
                        e_2 = _a.sent();
                        logger.error('Proposition extensions initialisation failed', e_2);
                        throw e_2;
                    case 4: return [2 /*return*/];
                }
            });
        });
    };
    CoreVideoSdk.prefetch = function (sessionItem, includeAdsPrefetchWhenPossible) {
        if (!isRawSessionItem(sessionItem)) {
            var config = InternalConfigProvider.getForPlaybackType(CoreVideoInternal.config, sessionItem.type);
            var precursor = new SessionControllerPrecursor(CoreVideoInternal.videoPlatformIntegration, sessionItem, CoreVideoInternal.getPropositionExtensions().addonFactories, config, CoreVideoInternal.getPlayerPrecursorEngine());
            SessionControllerPrecursorCache.getInstance().insert(sessionItem.key, precursor);
            var destroyHandler = function () {
                var cache = SessionControllerPrecursorCache.getInstance();
                cache.clearCache();
            };
            return precursor.prefetch(destroyHandler, includeAdsPrefetchWhenPossible);
        }
        return Promise.resolve(undefined);
    };
    CoreVideoSdk.getPrefetchItems = function () {
        var cache = SessionControllerPrecursorCache.getInstance();
        var precursorKey = cache.findOldest();
        var precursor = cache.get(precursorKey);
        if (precursor && precursor.getPrefetchItem()) {
            // In the future, we will implement a list of prefetch items, but for now there is going to be only 1.
            // So even there is only 1 item, we are keeping it as a list to avoid breaking changes later
            return [precursor.getPrefetchItem()];
        }
        return undefined;
    };
    CoreVideoSdk.getPlayerController = function (playerEngineOptions) {
        var playerEngine = CoreVideoSdk.getPlayerEngine(playerEngineOptions);
        return new PlayerController(playerEngine, this.reInitialise.bind(this), CoreVideoInternal.videoPlatformIntegration, CoreVideoInternal.getPropositionExtensions(), playerEngineOptions, CoreVideoInternal.getPlayerPrecursorEngine(), this.hdcpManager);
    };
    return CoreVideoSdk;
}(CoreVideoInternal));
export { CoreVideoSdk };
//# sourceMappingURL=core-video-sdk-js.js.map